import { TabStyles, TextLinkWidget } from '@tableau/widgets';
import * as React from 'react';

import { getAppStore } from '~src/app/AppStore';
import { CompositeSessionInfoSelectors } from '~src/app/selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '~src/app/selectors/SiteUrlsSelectors';
import { Formatters, Messages } from '~src/codegen/Localize';
import { imgPath } from '~src/common/utilities/ImgUrl';
import { openReactActionDialog, ReactActionDialogBodyProps } from '~src/commonInfra/dialogs/ReactActionDialog';
import { cssClass } from '~src/style';
import { FlowOutputStepInfo } from '~src/ts/models/FlowOutputStepInfo';
import { RunFlowTaskInfo } from '~src/ts/models/RunFlowTaskInfo';

import { editFlowRunTaskDialog } from '../utils/FlowDialogUtils';
import { FlowTaskInfoItem } from './FlowTaskInfoItem';
import { FlowTaskOutputsGrid } from './FlowTaskOutputsGrid';

const rootClass = 'FlowTaskOutputsDialog';

export const headerStyle = cssClass(rootClass, 'headerStyle', {
  display: 'flex',
  paddingBottom: 8,
  marginBottom: 12,
  borderBottom: `1px solid ${TabStyles.Colors.L6}`,
  alignItems: 'center',
});

export const scheduleIconWrapper = cssClass(rootClass, 'scheduleIconWrapper', {
  display: 'flex',
  alignItems: 'center',
  fontWeight: 500,
  marginRight: 8,
});

export const scheduleIcon = cssClass(rootClass, 'scheduleIcon', {
  height: 20,
  width: 20,
  marginRight: 4,
});

function onAction(props: FlowTaskOutputsDialogProps) {
  return editFlowRunTaskDialog(props.flowTask);
}

export function openFlowTaskOutputsDialog(props: FlowTaskOutputsDialogProps) {
  const outputStepCount = props.flowTask.flowOutputSteps.length;
  const modalInstance = openReactActionDialog({
    title: Messages.outputsCount({COUNT: outputStepCount, COUNT_LOC: Formatters.formatCount(outputStepCount)}),
    bodyComponent: FlowTaskOutputsDialogContent,
    initialBodyProps: {
      ...props,
    },
    initialDialogState: {},
    actionName: Messages.editTask(),
    action: onAction,
    dialogWidth: 800,
    dismissDialogOnActionFailed: false,
  });

  return modalInstance.result;
}

export interface FlowTaskOutputsDialogProps {
  flowTask: RunFlowTaskInfo;
  outputSteps: ReadonlyArray<FlowOutputStepInfo>;
}

export type FlowTaskOutputsActionDialogProps = ReactActionDialogBodyProps<FlowTaskOutputsDialogProps, {}>;

export function FlowTaskOutputsDialogContent(props: FlowTaskOutputsActionDialogProps): JSX.Element {
  function renderScheduleText(): JSX.Element {
    const siteUrls = siteUrlsSelector(getAppStore().getState());
    const isUserSiteOrServerAdmin = CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector(getAppStore().getState());
    return isUserSiteOrServerAdmin ?
      <TextLinkWidget
        href={siteUrls.scheduleFlowsUrl(props.flowTask.schedule.id)}
        title={props.flowTask.schedule.name}
        testId={`flow-task-outputs-dialog-link-${props.flowTask.schedule.id}`}
      >
        {props.flowTask.schedule.name}
      </TextLinkWidget>
    :
      <div title={props.flowTask.schedule.name}>
        {props.flowTask.schedule.name}
      </div>;
  }
  const outputIds = props.flowTask.flowOutputSteps.map(outputStep => outputStep.id);

  return (
    <div>
      <div className={headerStyle}>
        <div className={scheduleIconWrapper}>
          <img src={imgPath('schedule_content_type_small.svg')} className={scheduleIcon} alt={''} />
          {Messages.schedule()}
        </div>
        <div>{renderScheduleText()}</div>
      </div>
      <FlowTaskInfoItem
        taskId={props.flowTask.id}
        flowName={props.flowTask.flow.name}
        flowId={props.flowTask.flow.id}
        lastRun={props.flowTask.lastRunAt}
        taskStatus={props.flowTask.status}
        expansionContent={<FlowTaskOutputsGrid outputIds={outputIds} outputSteps={props.outputSteps} />}
        startExpanded={true}
      />
    </div>
  );
}
